import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { trackPurchase } from '../../lib/analytics';
import { tx } from '@/translations/translations';

const PAY_LABELS = { paypal: 'PayPal', mercadopago: 'Mercado Pago' };

export const OrderSuccessScreen = ({ transferSubmitted, lastOrderId, paymentMethod, language, cartItems, lastOrderTotal, whatsappUrl, onClose }) => {
    const orderId = lastOrderId || 'N/A';
    React.useEffect(() => { trackPurchase(lastOrderTotal || 0); }, []);
    const paidLabel = PAY_LABELS[paymentMethod] || tx(language, 'Transferencia', 'Bank transfer', 'העברה בנקאית');
    const itemCount = cartItems.reduce((s, i) => s + i.quantity, 0);
    const orderIdShort = String(orderId).slice(-8).toUpperCase();

    return (
      <div className="fixed inset-0 bg-black/70 z-[60] overflow-y-auto p-3 sm:p-4">
        <div className="min-h-full flex items-start justify-center pt-4 sm:pt-12">
          <Card className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl">
            {/* Success hero */}
            <div className="relative bg-gradient-to-br from-emerald-500 via-green-500 to-emerald-600 px-6 py-8 text-center text-white overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 bg-lime-300/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-emerald-200/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <Check className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600" strokeWidth={3} />
                </div>
                <h2 className="text-xl sm:text-2xl font-black mb-1 tracking-tight">
                  {transferSubmitted
                    ? tx(language, '¡Pedido registrado!', 'Order registered!', 'ההזמנה נרשמה!')
                    : tx(language, '¡Pago exitoso!', 'Payment successful!', 'התשלום בוצע בהצלחה!')}
                </h2>
                <p className="text-emerald-50 text-xs sm:text-sm">
                  {tx(language, 'Gracias por tu compra · Mitzva Fit', 'Thanks for your purchase · Mitzva Fit', 'תודה על הקנייה · Mitzva Fit')}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-semibold">
                  <span className="opacity-80">{tx(language, 'Pedido', 'Order', 'הזמנה')}:</span>
                  <span className="tracking-widest">#{orderIdShort}</span>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="px-5 sm:px-6 py-5 space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-slate-50 py-3 px-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    {tx(language, 'Productos', 'Items', 'מוצרים')}
                  </p>
                  <p className="text-lg font-black text-slate-900">{itemCount}</p>
                </div>
                <div className="rounded-xl bg-slate-50 py-3 px-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    {tx(language, 'Total', 'Total', 'סה״כ')}
                  </p>
                  <p className="text-lg font-black text-slate-900">${lastOrderTotal?.toFixed(0) || '0'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 py-3 px-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    {tx(language, 'Método', 'Method', 'אמצעי')}
                  </p>
                  <p className="text-xs font-bold text-slate-900 pt-0.5">{paidLabel}</p>
                </div>
              </div>

              {/* Next steps */}
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
                  {tx(language, '¿Qué sigue?', 'What happens next?', 'מה הלאה?')}
                </p>
                <ul className="space-y-2.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[11px] font-bold flex items-center justify-center mt-0.5">1</span>
                    <span>{tx(language, 'Recibirás un email de confirmación', 'You will receive a confirmation email', 'תקבלו אימייל אישור')}</span>
                  </li>
                  {transferSubmitted ? (
                    <li className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[11px] font-bold flex items-center justify-center mt-0.5">2</span>
                      <span>{tx(language, 'Te contactaremos al verificar tu pago', 'We will contact you once payment is verified', 'ניצור קשר לאחר אימות התשלום')}</span>
                    </li>
                  ) : (
                    <li className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[11px] font-bold flex items-center justify-center mt-0.5">2</span>
                      <span>{tx(language, 'Preparamos tu pedido con dedicación', 'We prepare your order with devotion', 'אנו מכינים את הזמנתכם במסירות')}</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[11px] font-bold flex items-center justify-center mt-0.5">3</span>
                    <span>{tx(language, 'Tu envío llega en 1-5 días hábiles', 'Your shipment arrives in 1-5 business days', 'המשלוח מגיע תוך 1-5 ימי עסקים')}</span>
                  </li>
                </ul>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold py-3 rounded-xl transition-colors shadow-sm"
                    data-testid="whatsapp-notify-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {tx(language, '¡Ya pagué! Avisar por WhatsApp', 'I paid! Notify via WhatsApp', 'שילמתי! עדכון בוואטסאפ')}
                  </a>
                )}
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="w-full border-slate-300 hover:bg-slate-50 text-slate-700 py-3 rounded-xl"
                  data-testid="close-success"
                >
                  {tx(language, 'Seguir Comprando', 'Continue Shopping', 'המשך בקנייה')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
};
